import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { usePropertyField, useProperty } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
  poi_map_icon?: string;
  poi_google_place_id?: string;
  poi_place_primary_type?: string;
}

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

function formatDistance(raw: string): string {
  const trimmed = raw.trim();
  if (/mi$/i.test(trimmed)) return trimmed;
  if (/ft$/i.test(trimmed)) return trimmed;
  if (/km$/i.test(trimmed)) return trimmed;
  return `${trimmed} mi`;
}

function formatMinutes(raw: string, mode: string): string {
  const trimmed = raw.trim();
  if (/min/i.test(trimmed)) return trimmed;
  return `${trimmed} min ${mode}`;
}

function getPrimaryDistance(poi: POI): string | null {
  if (hasVal(poi.poi_miles_from_property)) return formatDistance(poi.poi_miles_from_property);
  if (hasVal(poi.poi_driving_minutes)) return formatMinutes(poi.poi_driving_minutes, 'drive');
  if (hasVal(poi.poi_walking_minutes)) return formatMinutes(poi.poi_walking_minutes, 'walk');
  return null;
}

function getSecondaryDistances(poi: POI): string[] {
  const items: string[] = [];
  if (hasVal(poi.poi_walking_minutes)) items.push(`${formatMinutes(poi.poi_walking_minutes, 'walk')} 🚶`);
  if (hasVal(poi.poi_driving_minutes)) items.push(`${formatMinutes(poi.poi_driving_minutes, 'drive')} 🚗`);
  if (hasVal(poi.poi_biking_minutes)) items.push(`${formatMinutes(poi.poi_biking_minutes, 'bike')} 🚲`);
  return items;
}

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

// ============================================================================
// Map styling & markers
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) { reject(new Error('No key')); return; }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => { delete (window as any)[cb]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => { mapsLoaderPromise = null; reject(new Error('Script failed')); };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 30): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.3)}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makeHighlightMarker(fillColor: string, glyphSvg: string, size = 40): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.35)}" viewBox="0 0 40 54">
  <ellipse cx="20" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M20 0C9 0 0 9 0 20c0 15 20 31 20 31s20-16 20-31C40 9 31 0 20 0z" fill="${fillColor}" stroke="#fff" stroke-width="2"/>
  <g transform="translate(10,10)" fill="#fff"><g transform="translate(10,10) scale(0.7) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makePropertyPinBg(primaryColor: string, hasIcon: boolean): string {
  const inner = hasIcon
    ? `<circle cx="30" cy="27" r="18" fill="#fff"/>`
    : `<circle cx="30" cy="27" r="18" fill="#fff" opacity="0.95"/>
       <path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="${primaryColor}"/>
       <path d="M21 27l9-8 9 8" fill="none" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${primaryColor}" stroke="#fff" stroke-width="2.5"/>
  ${inner}
</svg>`)}`;
}

const CATEGORY_ICONS: Record<string, { glyph: string; hue: number }> = {
  restaurant: { glyph: '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>', hue: 0 },
  shopping: { glyph: '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>', hue: 270 },
  education: { glyph: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>', hue: 200 },
  health: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  fitness: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  entertainment: { glyph: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>', hue: 45 },
  park: { glyph: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>', hue: 140 },
  transit: { glyph: '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>', hue: 210 },
  grocery: { glyph: '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>', hue: 120 },
  default: { glyph: '<circle cx="8" cy="8" r="4" fill="#fff"/>', hue: 0 },
};

function getCategoryStyle(poiType: string, primaryHue: number): { glyph: string; hue: number } {
  const lower = (poiType || '').toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_ICONS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return val;
  }
  return { ...CATEGORY_ICONS.default, hue: (primaryHue + 180) % 360 };
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q-p)*6*t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q-p)*(2/3-t)*6;
    return p;
  };
  const q = l < 0.5 ? l*(1+s) : l+s-l*s;
  const p = 2*l - q;
  const r = Math.round(hue2rgb(p,q,h+1/3)*255);
  const g = Math.round(hue2rgb(p,q,h)*255);
  const b = Math.round(hue2rgb(p,q,h-1/3)*255);
  return `#${((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)}`;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1],16)/255, g = parseInt(m[2],16)/255, b = parseInt(m[3],16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  if (max === min) return 0;
  const d = max-min;
  let h = 0;
  if (max === r) h = ((g-b)/d + (g<b?6:0))/6;
  else if (max === g) h = ((b-r)/d+2)/6;
  else h = ((r-g)/d+4)/6;
  return Math.round(h*360);
}

function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// ============================================================================
// Google Map component
// ============================================================================

function GoogleMap({
  propertyLat, propertyLng, propertyIcon, primaryColor, pois, activeTab,
  selectedPoiIndex, onMarkerClick,
}: {
  propertyLat: number;
  propertyLng: number;
  propertyIcon: string;
  primaryColor: string;
  pois: POI[];
  activeTab: string;
  selectedPoiIndex: number | null;
  onMarkerClick: (index: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; normalIcon: any; highlightIcon: any; poiIndex: number }[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const primaryHue = useMemo(() => hexToHue(primaryColor), [primaryColor]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !containerRef.current) return;
      const g = (window as any).google.maps;
      const map = new g.Map(containerRef.current, {
        center: { lat: propertyLat, lng: propertyLng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
        styles: buildMapStyles(primaryColor),
      });

      infoWindowRef.current = new g.InfoWindow();

      const hasIcon = !!propertyIcon;
      new g.Marker({
        position: { lat: propertyLat, lng: propertyLng },
        map, title: 'Property', zIndex: 999,
        icon: {
          url: makePropertyPinBg(primaryColor, hasIcon),
          scaledSize: new g.Size(60, 74),
          anchor: new g.Point(30, 74),
        },
      });
      if (hasIcon) {
        new g.Marker({
          position: { lat: propertyLat, lng: propertyLng },
          map, zIndex: 1000, clickable: false,
          icon: {
            url: propertyIcon,
            scaledSize: new g.Size(24, 24),
            anchor: new g.Point(12, 59),
          },
        });
      }
      mapRef.current = map;
      setReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyLat, propertyLng, primaryColor, propertyIcon]);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const visible = activeTab === 'all'
      ? pois.map((p, i) => ({ poi: p, origIndex: i }))
      : pois.map((p, i) => ({ poi: p, origIndex: i })).filter(({ poi }) => (poi.poi_type || 'Nearby') === activeTab);

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;
    const iconCache = new Map<string, { normal: any; highlight: any }>();

    for (const { poi, origIndex } of visible) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };
      bounds.extend(pos);
      hasPoiBounds = true;

      const cat = poi.poi_type || 'Nearby';
      if (!iconCache.has(cat)) {
        const style = getCategoryStyle(cat, primaryHue);
        iconCache.set(cat, {
          normal: { url: makeSvgMarker('#888', style.glyph, 28), scaledSize: new g.Size(28, 36), anchor: new g.Point(14, 36) },
          highlight: { url: makeHighlightMarker(primaryColor, style.glyph, 40), scaledSize: new g.Size(40, 54), anchor: new g.Point(20, 54) },
        });
      }

      const icons = iconCache.get(cat)!;
      const isSelected = selectedPoiIndex === origIndex;

      const marker = new g.Marker({
        position: pos, map,
        title: poi.poi_name || '',
        icon: isSelected ? icons.highlight : icons.normal,
        zIndex: isSelected ? 900 : 1,
        animation: isSelected ? g.Animation.BOUNCE : null,
      });

      if (isSelected) setTimeout(() => marker.setAnimation(null), 1400);

      marker.addListener('click', () => onMarkerClick(origIndex));

      markersRef.current.push({ marker, normalIcon: icons.normal, highlightIcon: icons.highlight, poiIndex: origIndex });
    }

    if (hasPoiBounds) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      const listener = g.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        g.event.removeListener(listener);
      });
    }
  }, [pois, activeTab, propertyLat, propertyLng, primaryHue, primaryColor, selectedPoiIndex, onMarkerClick]);

  useEffect(() => { if (ready) syncMarkers(); }, [ready, syncMarkers]);

  // When selected POI changes, update marker icons and pan
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map || !ready) return;

    for (const entry of markersRef.current) {
      const isSelected = entry.poiIndex === selectedPoiIndex;
      entry.marker.setIcon(isSelected ? entry.highlightIcon : entry.normalIcon);
      entry.marker.setZIndex(isSelected ? 900 : 1);
      if (isSelected) {
        entry.marker.setAnimation(g.Animation.BOUNCE);
        setTimeout(() => entry.marker.setAnimation(null), 1400);
        map.panTo(entry.marker.getPosition());
      }
    }
  }, [selectedPoiIndex, ready]);

  return (
    <div className="w-full h-full relative rounded overflow-hidden">
      <div ref={containerRef} className="w-full h-full" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5]">
          <span className="font-body text-sm text-[var(--vibe-color-text-muted)] animate-pulse">Loading map…</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// POI list thumbnail — falls back to a location-pin icon if the image fails
// to load. (VibesitesImage's onError forwards no DOM event, so we track the
// error in state rather than mutating the element directly.)
// ============================================================================

function PoiThumb({ url }: { url: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <span className="nb-poi-thumb-fallback">
        <svg width="20" height="20" viewBox="0 0 14 14" fill="none"><path d="M7 1C4.79 1 3 2.79 3 5c0 3 4 7 4 7s4-4 4-7c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 117 3.5a1.5 1.5 0 010 3z" fill="currentColor"/></svg>
      </span>
    );
  }
  return (
    <VibesitesImage
      url={url}
      alt=""
      className="nb-poi-thumb"
      onError={() => setErrored(true)}
    />
  );
}

// ============================================================================
// POI Detail Panel
// ============================================================================

function POIDetailPanel({ poi, primaryColor }: { poi: POI; primaryColor: string; onClose: () => void }) {
  const distances = getSecondaryDistances(poi);
  const description = hasVal(poi.poi_short_description)
    ? stripHtml(poi.poi_short_description)
    : hasVal(poi.poi_long_description)
      ? stripHtml(poi.poi_long_description).slice(0, 300) + (stripHtml(poi.poi_long_description).length > 300 ? '…' : '')
      : null;

  const hasImage = !!poi.poi_featured_image;
  const hasInfo = hasVal(poi.poi_address) || hasVal(poi.poi_business_phone) || distances.length > 0;
  const hasActions = hasVal(poi.poi_business_url) || hasVal(poi.poi_directions_map_url);
  const [imageError, setImageError] = useState(false);

  return (
    <div className="nb-detail-panel">
      {hasImage && !imageError && (
        <VibesitesImage
          url={poi.poi_featured_image!}
          alt={poi.poi_name || ''}
          className="nb-detail-image"
          onError={() => setImageError(true)}
        />
      )}

      {hasVal(poi.poi_place_primary_type) && (
        <span className="nb-detail-badge" style={{ background: `${primaryColor}15`, color: 'var(--vibe-color-text)' }}>
          {poi.poi_place_primary_type!.replace(/_/g, ' ')}
        </span>
      )}

      {description && (
        <p className="font-body text-xs text-[var(--vibe-color-text-muted)] mt-2 leading-relaxed">{description}</p>
      )}

      {hasInfo && (
        <div className="nb-detail-info">
          {hasVal(poi.poi_address) && (
            <div className="nb-detail-row">
              <svg className="nb-detail-row-icon" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1C4.79 1 3 2.79 3 5c0 3 4 7 4 7s4-4 4-7c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 117 3.5a1.5 1.5 0 010 3z" fill="currentColor"/></svg>
              <span className="font-body text-xs text-[var(--vibe-color-text-muted)]">{poi.poi_address}</span>
            </div>
          )}
          {hasVal(poi.poi_business_phone) && (
            <div className="nb-detail-row">
              <svg className="nb-detail-row-icon" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.8 1.4A.7.7 0 013.5 1h2.1a.7.7 0 01.67.5l.53 1.6a.7.7 0 01-.18.72L5.5 4.94a8.4 8.4 0 003.56 3.56l1.12-1.12a.7.7 0 01.72-.18l1.6.53a.7.7 0 01.5.67v2.1a.7.7 0 01-.7.7A10.5 10.5 0 012.1 1.7a.7.7 0 01.7-.7v.4z" fill="currentColor"/></svg>
              <a href={`tel:${poi.poi_business_phone}`} className="font-body text-xs font-medium text-[var(--vibe-color-text)]">
                {poi.poi_business_phone}
              </a>
            </div>
          )}
          {distances.length > 0 && (
            <div className="nb-detail-distances">
              {distances.map((d, i) => (
                <span key={i} className="nb-detail-dist-tag">{d}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {hasActions && (
        <div className="nb-detail-actions">
          {hasVal(poi.poi_business_url) && (
            <a href={poi.poi_business_url!} target="_blank" rel="noopener noreferrer" className="nb-detail-btn" style={{ background: primaryColor }}>
              Visit Website
            </a>
          )}
          {hasVal(poi.poi_directions_map_url) && (
            <a href={poi.poi_directions_map_url!} target="_blank" rel="noopener noreferrer" className="nb-detail-btn nb-detail-btn--outline" style={{ borderColor: primaryColor }}>
              Directions
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function NeighborhoodMap() {
  const property = useProperty();
  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', { collection: 'neighborhood_pois' });

  const propLat = usePropertyField('property_address_latitude');
  const propLng = usePropertyField('property_address_longitude');
  const propertyIcon = (property?.property_icon as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#4a7c59';

  const allPois = pois && pois.length > 0 ? pois : [];

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const poi of allPois) cats.add(poi.poi_type || 'Nearby');
    return Array.from(cats);
  }, [allPois]);

  const [activeTab, setActiveTab] = useState('all');
  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number | null>(null);
  const [detailPoiIndex, setDetailPoiIndex] = useState<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const filteredPois = useMemo(() => {
    if (activeTab === 'all') return allPois.map((p, i) => ({ poi: p, origIndex: i }));
    return allPois.map((p, i) => ({ poi: p, origIndex: i })).filter(({ poi }) => (poi.poi_type || 'Nearby') === activeTab);
  }, [allPois, activeTab]);

  const handleListItemClick = useCallback((origIndex: number) => {
    const deselecting = selectedPoiIndex === origIndex;
    setSelectedPoiIndex(deselecting ? null : origIndex);
    setDetailPoiIndex(deselecting ? null : origIndex);
  }, [selectedPoiIndex]);

  const handleMarkerClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex(origIndex);
    setDetailPoiIndex(origIndex);
    const el = listRef.current?.querySelector(`[data-poi-index="${origIndex}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setSelectedPoiIndex(null);
    setDetailPoiIndex(null);
  }, []);

  const lat = parseFloat(propLat as string || '');
  const lng = parseFloat(propLng as string || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  const allTabs = [{ key: 'all', label: 'All' }, ...categories.map((c) => ({ key: c, label: c }))];
  const activeLabel = allTabs.find((t) => t.key === activeTab)?.label || 'All';

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {categories.length > 0 && (
          <>
            {/* Desktop: horizontal tab bar */}
            <div className="nb-tabs nb-tabs--desktop">
              {allTabs.map((tab) => (
                <button key={tab.key} className={`nb-tab${activeTab === tab.key ? ' nb-tab--active' : ''}`} onClick={() => handleTabChange(tab.key)}>{tab.label}</button>
              ))}
            </div>

            {/* Mobile: dropdown selector */}
            <div className="nb-filter-mobile" ref={dropdownRef}>
              <button className="nb-filter-trigger" onClick={() => setDropdownOpen(!dropdownOpen)}>
                <span className="nb-filter-label">Category</span>
                <span className="nb-filter-value">{activeLabel}</span>
                <svg className={`nb-filter-chevron${dropdownOpen ? ' nb-filter-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {dropdownOpen && (
                <div className="nb-filter-dropdown">
                  {allTabs.map((tab) => (
                    <button
                      key={tab.key}
                      className={`nb-filter-option${activeTab === tab.key ? ' nb-filter-option--active' : ''}`}
                      onClick={() => { handleTabChange(tab.key); setDropdownOpen(false); }}
                    >
                      {tab.label}
                      {activeTab === tab.key && <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="nb-layout">
          <div className="nb-map-container">
            {hasCoords ? (
              <GoogleMap
                propertyLat={lat} propertyLng={lng}
                propertyIcon={propertyIcon} primaryColor={primaryColor}
                pois={allPois} activeTab={activeTab}
                selectedPoiIndex={selectedPoiIndex}
                onMarkerClick={handleMarkerClick}
              />
            ) : (
              <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center text-[var(--vibe-color-text-muted)]">
                <p className="font-body">Map coordinates not available</p>
              </div>
            )}
          </div>

          <div className="nb-list-container">
            <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 pr-1 nb-poi-list">
              {filteredPois.length > 0 ? (
                filteredPois.map(({ poi, origIndex }) => {
                  const dist = getPrimaryDistance(poi);
                  const isActive = selectedPoiIndex === origIndex;
                  const isDetail = detailPoiIndex === origIndex;
                  return (
                    <div key={origIndex} data-poi-index={origIndex}>
                      <div
                        className={`nb-poi-card${isActive ? ' nb-poi-card--active' : ''}${isDetail ? ' nb-poi-card--expanded' : ''}`}
                        style={isActive ? { borderLeftColor: primaryColor } : undefined}
                        onClick={() => handleListItemClick(origIndex)}
                      >
                        <div className="nb-poi-card-main">
                          {poi.poi_featured_image && (
                            <PoiThumb url={poi.poi_featured_image} />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-body text-sm font-semibold text-[var(--vibe-color-text)] leading-tight">
                              {poi.poi_name}
                            </p>
                            {hasVal(poi.poi_address) && (
                              <p className="font-body text-xs text-[var(--vibe-color-text-muted)] mt-0.5 truncate">{poi.poi_address}</p>
                            )}
                          </div>
                          {dist && (
                            <span className="font-body text-xs font-medium whitespace-nowrap ml-3 shrink-0 text-[var(--vibe-color-text)]">{dist}</span>
                          )}
                        </div>
                        <button
                          className="nb-view-details-btn"
                          style={{ color: 'var(--vibe-color-text)', borderColor: `${primaryColor}40` }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const closing = isDetail;
                            setDetailPoiIndex(closing ? null : origIndex);
                            setSelectedPoiIndex(closing ? null : origIndex);
                          }}
                        >
                          {isDetail ? (
                            <><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 7.5L6 4.5L3 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> Hide Details</>
                          ) : (
                            <><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> View Details</>
                          )}
                        </button>
                      </div>
                      {isDetail && <POIDetailPanel poi={poi} primaryColor={primaryColor} onClose={() => setDetailPoiIndex(null)} />}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
                  <p className="font-body text-lg">No points of interest found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ===== Layout ===== */
        .nb-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          max-width: 100%;
          overflow: hidden;
        }
        @media (min-width: 1024px) {
          .nb-layout { grid-template-columns: 1fr 1fr; }
        }
        .nb-map-container {
          width: 100%;
          min-width: 0;
          aspect-ratio: 4/3;
          border-radius: 6px;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 1024px) {
          .nb-map-container { aspect-ratio: auto; min-height: 450px; }
        }
        .nb-list-container {
          display: flex;
          flex-direction: column;
          min-width: 0;
          max-height: 60vh;
        }
        @media (min-width: 1024px) {
          .nb-list-container { max-height: 550px; min-height: 450px; }
        }

        /* ===== Desktop tabs ===== */
        .nb-tabs--desktop {
          display: none;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }
        @media (min-width: 768px) {
          .nb-tabs--desktop { display: flex; }
        }
        .nb-tab {
          font-family: var(--vibe-font-body); font-size: 0.8rem; font-weight: 500;
          text-transform: uppercase; letter-spacing: 0.08em; padding: 0.5rem 1.25rem;
          border: 1px solid var(--vibe-color-border); background: transparent;
          color: var(--vibe-color-text-muted); cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .nb-tab:first-child { border-radius: 3px 0 0 3px; }
        .nb-tab:last-child { border-radius: 0 3px 3px 0; }
        .nb-tab:hover { color: var(--vibe-color-text); border-color: var(--vibe-color-text-muted); }
        .nb-tab--active { background: var(--vibe-color-primary); border-color: var(--vibe-color-primary); color: var(--vibe-color-text-on-primary); }
        .nb-tab--active:hover { color: var(--vibe-color-text-on-primary); border-color: var(--vibe-color-primary); }

        /* ===== Mobile filter dropdown ===== */
        .nb-filter-mobile {
          display: block;
          position: relative;
          margin-bottom: 1rem;
        }
        @media (min-width: 768px) {
          .nb-filter-mobile { display: none; }
        }
        .nb-filter-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.75rem 1rem;
          background: white;
          border: 1px solid var(--vibe-color-border);
          border-radius: 6px;
          cursor: pointer;
          font-family: var(--vibe-font-body);
          transition: border-color 0.2s;
        }
        .nb-filter-trigger:hover { border-color: var(--vibe-color-text-muted); }
        .nb-filter-label {
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text-muted);
        }
        .nb-filter-value {
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          flex: 1;
          text-align: right;
          margin-right: 0.5rem;
        }
        .nb-filter-chevron {
          color: var(--vibe-color-text-muted);
          transition: transform 0.2s;
          flex-shrink: 0;
        }
        .nb-filter-chevron--open { transform: rotate(180deg); }

        .nb-filter-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0; right: 0;
          background: white;
          border: 1px solid var(--vibe-color-border);
          border-radius: 6px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          z-index: 20;
          overflow: hidden;
          animation: nb-drop-in 0.15s ease;
        }
        .nb-filter-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.75rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          background: transparent;
          border: none;
          border-bottom: 1px solid var(--vibe-color-border);
          color: var(--vibe-color-text);
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
        }
        .nb-filter-option:last-child { border-bottom: none; }
        .nb-filter-option:hover { background: #f8f8f8; }
        .nb-filter-option--active {
          color: var(--vibe-color-primary);
          font-weight: 600;
        }

        /* ===== POI list ===== */
        .nb-poi-list::-webkit-scrollbar { width: 4px; }
        .nb-poi-list::-webkit-scrollbar-track { background: transparent; }
        .nb-poi-list::-webkit-scrollbar-thumb { background: var(--vibe-color-border); border-radius: 2px; }

        .nb-poi-card {
          display: flex; flex-direction: column;
          padding: 0.75rem; background: white; border: 1px solid var(--vibe-color-border);
          border-left: 4px solid var(--vibe-color-border); border-radius: 6px;
          cursor: pointer; transition: all 0.2s ease;
          animation: nb-fade-in 0.3s ease both;
        }
        .nb-poi-card:hover { background: #fafafa; border-left-color: #bbb; }
        .nb-poi-card--active {
          background: color-mix(in srgb, var(--vibe-color-primary) 5%, white);
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        }
        .nb-poi-card--expanded { border-radius: 6px 6px 0 0; }

        .nb-poi-card-main {
          display: flex; align-items: center; gap: 0.75rem;
        }

        .nb-poi-thumb {
          width: 44px; height: 44px; border-radius: 6px;
          object-fit: cover; flex-shrink: 0;
        }
        .nb-poi-thumb-fallback {
          width: 44px; height: 44px; border-radius: 6px;
          flex-shrink: 0; background: #f0f0f0;
          display: flex; align-items: center; justify-content: center;
          color: var(--vibe-color-text-muted);
        }

        .nb-view-details-btn {
          display: flex; align-items: center; justify-content: center; gap: 4px;
          width: 100%; margin-top: 0.6rem; padding: 0.4rem 0;
          font-family: var(--vibe-font-body); font-size: 0.7rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.06em;
          background: transparent; border: 1px solid; border-radius: 4px;
          cursor: pointer; transition: background 0.15s, color 0.15s;
        }
        .nb-view-details-btn:hover { background: rgba(0,0,0,0.02); }
        .nb-view-details-btn svg { flex-shrink: 0; }

        /* ===== Detail panel ===== */
        .nb-detail-panel {
          padding: 0.75rem; margin-bottom: 4px;
          background: #fafafa; border: 1px solid var(--vibe-color-border);
          border-top: none; border-radius: 0 0 6px 6px;
          animation: nb-slide-down 0.25s ease;
        }
        .nb-detail-badge {
          display: inline-block; font-family: var(--vibe-font-body);
          font-size: 10px; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.05em; padding: 2px 8px; border-radius: 3px; margin-top: 6px;
        }
        .nb-detail-image {
          width: 100%; height: 140px; object-fit: cover;
          border-radius: 6px;
        }
        .nb-detail-info {
          margin-top: 10px; padding-top: 8px;
          border-top: 1px solid var(--vibe-color-border);
        }
        .nb-detail-row { display: flex; align-items: flex-start; gap: 6px; margin-top: 6px; }
        .nb-detail-row:first-child { margin-top: 0; }
        .nb-detail-row-icon {
          flex-shrink: 0; margin-top: 1px;
          color: var(--vibe-color-text-muted);
        }
        .nb-detail-distances { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
        .nb-detail-dist-tag {
          font-family: var(--vibe-font-body); font-size: 11px;
          background: white; border: 1px solid var(--vibe-color-border);
          padding: 3px 10px; border-radius: 4px; white-space: nowrap;
        }
        .nb-detail-actions {
          display: flex; gap: 8px; margin-top: 10px; padding-top: 10px;
          border-top: 1px solid var(--vibe-color-border); flex-wrap: wrap;
        }
        .nb-detail-btn {
          font-family: var(--vibe-font-body); font-size: 11px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.04em;
          padding: 7px 16px; border-radius: 4px; text-decoration: none;
          color: var(--vibe-color-text-on-primary); border: none; cursor: pointer; transition: opacity 0.2s;
          flex: 1; text-align: center;
        }
        .nb-detail-btn:hover { opacity: 0.85; }
        .nb-detail-btn--outline { background: transparent !important; border: 1px solid; color: var(--vibe-color-text) !important; }

        /* ===== Animations ===== */
        @keyframes nb-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes nb-slide-down {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 400px; }
        }
        @keyframes nb-drop-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nb-poi-card, .nb-detail-panel, .nb-filter-dropdown { animation: none; }
        }

        /* ===== Google Maps info window cleanup ===== */
        .gm-style .gm-style-iw-c { border-radius: 6px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.12) !important; padding: 8px 12px !important; }
        .gm-style .gm-style-iw-d { overflow: hidden !important; }
        .gm-style .gm-style-iw-tc::after { background: white !important; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {},
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as any).contentSchema = contentSchema;

export default NeighborhoodMap;
